import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { ArrowUpTrayIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import { analyticsService } from 'src/services/analytics/analyticsService';
import { RFQ_BUILDER_FILES_UPLOAD_COMPLETED } from 'src/services/analytics/rfqBuilderEvents';
import { Rfq } from 'src/shared/types/rfq';
import {
  isImageMimeType,
  isUnprocessableForRfqExtraction
} from 'src/shared/utils/fileTypeUtils';

import { cn, Drawer, DrawerContent, Progress } from 'src/shared/components/ui';
import { ButtonV2, Typography } from 'src/shared/components/ui';

import { useRfqConversationContext } from '../../context/RfqConversationContext';
import { useCaptionModal } from '../../hooks/useCaptionModal';
import { useFileCaptionManager } from '../../hooks/useFileCaptionManager';
import { useFilePreview } from '../../hooks/useFilePreview';
import { useOptimisticFileUploadWithCaption } from '../../hooks/useOptimisticFileUploadWithCaption';
import { CaptionModal } from './CaptionModal';
import { FilePreview } from './FilePreview';
import { ImagePreview } from './ImagePreview';
import { UnprocessableFileWarning } from './UnprocessableFileWarning';
import { UploadFailedNotice } from './UploadFailedNotice';

interface RfqFilesUploadProps {
  isOpen: boolean;
  onClose: () => void;
  rfq?: Rfq;
  onSuccess?: () => void;
}

interface FileWithCaption {
  file: File;
  caption: string;
  preview: string;
  id: string;
  pendingFileId: string; // Maps to PendingFileWithCaption.id (empty string initially, set by useEffect)
  source: 'user' | 'brave'; // Track file origin: 'user' for uploads, 'brave' for search results
}

const RfqFilesUpload = ({
  isOpen,
  onClose,
  rfq,
  onSuccess
}: RfqFilesUploadProps) => {
  const [files, setFiles] = useState<FileWithCaption[]>([]);
  const [pendingFileUploads, setPendingFileUploads] = useState<
    FileWithCaption[]
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<FileWithCaption[]>([]);
  const [newFileIds, setNewFileIds] = useState<Set<string>>(new Set());
  const [isShowingNewFilesOnly, setIsShowingNewFilesOnly] = useState(false);
  const pendingEditFileIdRef = useRef<string | null>(null);

  // Reusable hooks
  const { setPreview, getPreview, removePreview, previewUrlsRef } =
    useFilePreview();
  const uploadHook = useOptimisticFileUploadWithCaption({
    rfqId: rfq?.rfq?.id
  });
  const {
    pendingFiles,
    addFiles: addFilesToUpload,
    removeFile: removeFileFromUpload,
    waitForUploads,
    isUploading: isUploadingFiles,
    uploadFileWithCaption,
    retryUpload
  } = uploadHook;

  const { getPendingFileByIdOrFile, updateCaptionForFile } =
    useFileCaptionManager(rfq?.rfq?.id, uploadHook);
  const { connectToConversation, addUserMessage, isSending, setIsSending } =
    useRfqConversationContext();
  // Filter files for modal: show only new files when in "new files" mode, otherwise show all
  // Also include pendingFileUploads if they exist
  const modalItems = useMemo(() => {
    if (pendingFileUploads.length > 0) {
      return pendingFileUploads;
    }
    if (isShowingNewFilesOnly && newFileIds.size > 0) {
      return files.filter((f) => newFileIds.has(f.id));
    }

    return files;
  }, [files, isShowingNewFilesOnly, newFileIds, pendingFileUploads]);

  const captionModal = useCaptionModal(modalItems);
  const {
    isCaptionModalOpen,
    onCloseCaptionModal,
    currentCaptionIndex,
    captionValue,
    setCaptionValue,
    isEditMode,
    handleEditCaption,
    handleCaptionNext: captionNext,
    handleCaptionPrevious: captionPrevious,
    handleCaptionDone: captionDone,
    openModalForNewItems
  } = captionModal;

  // Keep filesRef in sync with files state
  useEffect(() => {
    filesRef.current = files;
    // Ensure all preview URLs are stored in the ref
    files.forEach((f) => {
      if (f.preview && !previewUrlsRef.current.has(f.id)) {
        setPreview(f.id, f.preview);
      }
    });
  }, [files, setPreview]);

  // Handle opening modal for existing file after clearing new files flag
  useEffect(() => {
    if (pendingEditFileIdRef.current && !isShowingNewFilesOnly) {
      const fileId = pendingEditFileIdRef.current;
      const fileIndex = files.findIndex((f) => f.id === fileId);

      if (fileIndex !== -1) {
        handleEditCaption(fileIndex);
      }
      pendingEditFileIdRef.current = null;
    }
  }, [isShowingNewFilesOnly, files, handleEditCaption]);

  const [searchParams] = useSearchParams();
  const type = searchParams.get('files');

  const pageTitle =
    type === 'prepared-rfq'
      ? 'Great! Thanks for preparing your RFQ'
      : 'Share your references';
  const pageDescription =
    type === 'prepared-rfq'
      ? 'Upload RFQ and other supporting files'
      : 'Add any supporting files you have. We’ll ask a few follow-up questions before preparing supplier quotes.';

  const initialMessage =
    type === 'prepared-rfq' ? 'Here is my RFQ' : 'Here are my references';

  // Sync files with pendingFiles from hook - map new pendingFiles to files
  // Only sync pendingFileId mapping and captions, never touch preview
  useEffect(() => {
    setFiles((prevFiles) => {
      // Early return if no files to process
      if (prevFiles.length === 0) return prevFiles;

      // Find pendingFiles that don't have a corresponding file yet
      const existingPendingIds = new Set(
        prevFiles.filter((f) => f.pendingFileId).map((f) => f.pendingFileId!)
      );
      const newPendingFiles = pendingFiles.filter(
        (pf) => !existingPendingIds.has(pf.id)
      );

      let hasChanges = false;
      const result: FileWithCaption[] = [];

      // Process each existing file and update if needed
      for (const f of prevFiles) {
        // Get preview from ref if it exists, otherwise use the one from file
        const previewUrl = getPreview(f.id) || f.preview;

        if (previewUrl && !previewUrlsRef.current.has(f.id)) {
          setPreview(f.id, previewUrl);
        }

        let updatedFile: FileWithCaption = {
          file: f.file,
          caption: f.caption,
          preview: previewUrl, // Always use preview from ref or file
          id: f.id,
          pendingFileId: f.pendingFileId,
          source: f.source
        };

        // Map pendingFileId if missing
        if (!updatedFile.pendingFileId) {
          const matchingPendingFile = newPendingFiles.find(
            (pf) =>
              pf.file === f.file ||
              (pf.file.name === f.file.name &&
                pf.file.size === f.file.size &&
                pf.file.lastModified === f.file.lastModified)
          );

          if (matchingPendingFile) {
            hasChanges = true;
            updatedFile = {
              ...updatedFile,
              pendingFileId: matchingPendingFile.id
            };
          }
        }

        // Sync caption from pendingFiles if we have a pendingFileId
        if (updatedFile.pendingFileId) {
          const pendingFile = pendingFiles.find(
            (pf) => pf.id === updatedFile.pendingFileId
          );

          if (pendingFile && pendingFile.caption !== updatedFile.caption) {
            hasChanges = true;
            updatedFile = {
              ...updatedFile,
              caption: pendingFile.caption
            };
          }
        }

        // Always add the file (preserving all properties including preview)
        result.push(updatedFile);
      }

      // Only update if there are actual changes
      if (!hasChanges && result.length === prevFiles.length) {
        return prevFiles;
      }

      // Ensure we don't lose any files
      if (result.length !== prevFiles.length) {
        return prevFiles; // Return previous state if count doesn't match
      }

      return result;
    });
  }, [pendingFiles, getPreview, setPreview, previewUrlsRef]);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);

    // Create FileWithCaption entries with unique IDs (but don't add to files yet)
    const timestamp = Date.now();
    const newPendingFiles: FileWithCaption[] = fileArray.map((file, index) => {
      const fileId = `${file.name}-${file.size}-${file.lastModified}-${timestamp}-${index}-${Math.random()}`;
      const preview = URL.createObjectURL(file);

      // Store preview in ref immediately
      setPreview(fileId, preview);

      return {
        file,
        caption: '',
        preview, // Create preview immediately and store it
        id: fileId,
        pendingFileId: '', // Will be set when added to files
        source: 'user' // File uploaded by user
      };
    });

    // Store in pendingFileUploads instead of immediately adding to files
    setPendingFileUploads(newPendingFiles);

    // Open caption modal for new files (start at index 0 since they're pending)
    openModalForNewItems(0);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveFile = useCallback(
    (index: number) => {
      const fileToRemove = files[index];

      // Revoke preview URL and remove from ref
      removePreview(fileToRemove.id);

      // Remove from upload hook (handles RFQ deletion if needed)
      if (fileToRemove.pendingFileId) {
        removeFileFromUpload(fileToRemove.pendingFileId);
      }

      // Remove from local files state
      const newFiles = files.filter((_, i) => i !== index);

      setFiles(newFiles);

      // Adjust current index if needed
      if (currentCaptionIndex >= newFiles.length && newFiles.length > 0) {
        captionModal.setCurrentCaptionIndex(newFiles.length - 1);
      } else if (newFiles.length === 0) {
        onCloseCaptionModal();
      }
    },
    [
      files,
      currentCaptionIndex,
      removeFileFromUpload,
      removePreview,
      captionModal,
      onCloseCaptionModal
    ]
  );

  // Helper to get pending file by pendingFileId
  const getPendingFile = useCallback(
    (pendingFileId: string) => {
      return pendingFiles.find((pf) => pf.id === pendingFileId);
    },
    [pendingFiles]
  );

  // Add pending file to files and start upload
  const addPendingFile = useCallback(async () => {
    if (pendingFileUploads.length === 0) return;

    const currentPendingFile = pendingFileUploads[currentCaptionIndex];

    if (!currentPendingFile || !currentPendingFile.file) return;

    const fileToAdd = currentPendingFile.file;

    // Add file to upload hook (creates pending file with empty caption)
    addFilesToUpload([fileToAdd]);

    // Add to files with caption - the useEffect will map pendingFileId
    const fileToAddToState = {
      ...currentPendingFile,
      caption: captionValue
    };

    setFiles((prev) => {
      const updated = [...prev, fileToAddToState];

      return updated;
    });

    // Remove from pendingFileUploads
    setPendingFileUploads((prev) => {
      const updated = prev.filter((_, index) => index !== currentCaptionIndex);

      return updated;
    });

    // Find the pending file and update caption/trigger upload (even without caption)
    // Use a retry mechanism since React state updates are async
    const updatePendingFileCaption = async (retries = 5) => {
      for (let i = 0; i < retries; i++) {
        const pendingFile = getPendingFileByIdOrFile(undefined, fileToAdd);

        if (pendingFile) {
          // Update caption first
          await updateCaptionForFile(pendingFile, captionValue).catch(() => {
            // Silently handle errors
          });

          // If no caption, trigger upload directly (updateCaptionForFile only uploads if caption exists)
          if (!captionValue.trim() && pendingFile.status === 'pending') {
            await uploadFileWithCaption(pendingFile.id, captionValue).catch(
              () => {
                // Silently handle errors
              }
            );
          }

          return;
        }
        // Wait a bit before retrying
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    };

    updatePendingFileCaption();
  }, [
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    addFilesToUpload,
    getPendingFileByIdOrFile,
    updateCaptionForFile,
    uploadFileWithCaption
  ]);

  // Caption modal handlers
  const handleCaptionSave = useCallback(async () => {
    // Don't save if this is a pending file (only add when "Add" is clicked)
    if (pendingFileUploads.length > 0) {
      return;
    }

    // Get file from modalItems (which may be filtered)
    const currentModalFile = modalItems[currentCaptionIndex];

    if (!currentModalFile) return;

    // Find the actual file in the full files array by ID
    const currentFile = files.find((f) => f.id === currentModalFile.id);

    if (!currentFile) return;

    const pendingFile = getPendingFileByIdOrFile(
      currentFile.pendingFileId,
      currentFile.file
    );

    if (pendingFile) {
      await updateCaptionForFile(pendingFile, captionValue).catch(() => {
        // Silently handle errors - caption update will still be attempted
      });
    }
  }, [
    modalItems,
    files,
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    getPendingFileByIdOrFile,
    updateCaptionForFile
  ]);

  // Wrapper for handleEditCaption to clear new files flag when editing existing files
  const handleEditCaptionWrapper = useCallback(
    (index: number) => {
      const clickedFile = files[index];

      if (!clickedFile) return;

      // If we're currently showing only new files, clear the flag and wait for state update
      if (isShowingNewFilesOnly) {
        pendingEditFileIdRef.current = clickedFile.id;
        setIsShowingNewFilesOnly(false);
        setNewFileIds(new Set());
      } else {
        // Already showing all files, can edit directly
        handleEditCaption(index);
      }
    },
    [files, isShowingNewFilesOnly, handleEditCaption]
  );

  const handleCaptionNext = useCallback(async () => {
    // If this is a pending file, add it when "Add" is clicked
    if (pendingFileUploads.length > 0) {
      const hasMoreFiles = pendingFileUploads.length > 1;

      await addPendingFile();

      // If there are more pending files, move to next; otherwise close modal
      if (hasMoreFiles) {
        // Move to next pending file in the carousel (index stays the same since we removed current)
        setCaptionValue('');
      } else {
        // No more pending items, close modal
        setPendingFileUploads([]);
        onCloseCaptionModal();
      }

      return;
    }

    // For existing files, save caption and move to next
    await handleCaptionSave();
    await captionNext();
  }, [
    handleCaptionSave,
    captionNext,
    pendingFileUploads,
    addPendingFile,
    setCaptionValue,
    onCloseCaptionModal
  ]);

  const handleCaptionPrevious = useCallback(async () => {
    await handleCaptionSave();
    await captionPrevious();
  }, [handleCaptionSave, captionPrevious]);

  const handleCaptionDone = useCallback(async () => {
    // If this is a pending file, add it when "Add" is clicked
    if (pendingFileUploads.length > 0) {
      await addPendingFile();
      setPendingFileUploads([]);
      onCloseCaptionModal();

      return;
    }

    // For existing files, save caption and close
    await handleCaptionSave();
    // Clear new files flag when done
    setIsShowingNewFilesOnly(false);
    setNewFileIds(new Set());
    captionDone();
  }, [
    handleCaptionSave,
    captionDone,
    pendingFileUploads,
    addPendingFile,
    onCloseCaptionModal
  ]);

  const handleSend = async () => {
    if (!rfq?.rfq?.id || files.length === 0) return;

    // Save any pending caption if modal is open
    if (isCaptionModalOpen && files[currentCaptionIndex]) {
      await handleCaptionSave();
    }

    setIsSending(true);

    // Wait for all uploads to complete using hook's waitForUploads
    try {
      const uploadedFileIds = await waitForUploads();

      // Check if any files have errors
      const hasErrors = pendingFiles.some((pf) => pf.status === 'error');

      if (hasErrors) {
        setIsSending(false);

        return;
      }

      // The backend conversation model requires two turns:
      // 1. An initial message to start the conversation
      // 2. A follow-up message with the file references
      // We await the first call so the backend processes it before the second.
      const firstMessage = rfq?.rfq?.document?.name
        ? `Let's get started making an RFQ for a ${rfq.rfq.document.name}`
        : initialMessage;

      await connectToConversation(firstMessage, [], false);

      // Now send the files as a second turn and navigate immediately
      const filesMessage = rfq?.rfq?.document?.name
        ? `${initialMessage} for ${rfq.rfq.document.name}`
        : initialMessage;

      // Collect the full UploadedFile objects so the chat can display them
      const uploadedFiles = pendingFiles
        .filter((pf) => pf.status === 'completed' && pf.uploadedFile)
        .map((pf) => pf.uploadedFile!);

      addUserMessage(filesMessage, uploadedFiles);
      connectToConversation(filesMessage, uploadedFileIds, false);

      analyticsService.trackEvent(RFQ_BUILDER_FILES_UPLOAD_COMPLETED, {
        rfq_id: rfq?.rfq?.id,
        file_count: files.length
      });
      onSuccess?.();
    } catch (error) {
      console.error('Error waiting for uploads:', error);
      setIsSending(false);
    }
  };

  const isImage = (file: File) => isImageMimeType(file.type);

  // Render preview for caption modal
  const renderPreview = useCallback(
    (file: FileWithCaption, previewUrl: string | undefined) => {
      if (!previewUrl) return null;

      if (isImage(file.file)) {
        return (
          <ImagePreview
            src={previewUrl}
            alt={file.file.name}
            onError={() => {
              // Image failed to load - preview URL may have been revoked
            }}
          />
        );
      } else {
        return <FilePreview file={file.file} previewUrl={previewUrl} />;
      }
    },
    []
  );

  const getPreviewUrl = useCallback(
    (file: FileWithCaption) => {
      return getPreview(file.id) || file.preview;
    },
    [getPreview]
  );

  // Use full screen modal for non-image files (PDFs, docs, etc.)
  const isFullScreenPreview = useMemo(() => {
    const currentFile = modalItems[currentCaptionIndex];

    if (!currentFile) return false;

    return !isImage(currentFile.file);
  }, [modalItems, currentCaptionIndex]);

  // Show warning in caption modal if the current file can't be processed for RFQ creation
  const currentItemWarning = useMemo(() => {
    const currentFile = modalItems[currentCaptionIndex];

    if (!currentFile) return null;

    return isUnprocessableForRfqExtraction(currentFile.file.name) ? (
      <UnprocessableFileWarning variant="block" />
    ) : null;
  }, [modalItems, currentCaptionIndex]);

  return (
    <>
      <Drawer
        isKeyboardDismissDisabled
        motionProps={{
          initial: { x: '100%' },
          animate: { x: 0 },
          exit: { x: '100%' },
          transition: {
            type: 'tween',
            duration: 0.4,
            ease: [0.4, 0, 0.2, 1]
          }
        }}
        hideCloseButton
        size="full"
        placement="right"
        isOpen={isOpen}
        onClose={onClose}
      >
        <DrawerContent className="relative h-dvh w-full overflow mobile:overflow-y-auto">
          <div className="h-16 bg-transparent mobile:bg-white mobile:left-0 mobile:right-0 flex items-center gap-2 px-5 fixed top-0 z-[49]">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={onClose}
            >
              <ChevronLeftIcon className="size-5" />
              <Typography variant="body-1" weight="bold">
                Back
              </Typography>
            </div>
          </div>

          <div className="h-full pt-16 flex flex-col">
            <div className="flex-1 mobile:px-6 px-[10%] py-8 gap-10 grid grid-cols-2 mobile:grid-cols-1">
              {/* // left side */}
              <div className="flex flex-col gap-6">
                <Typography as="h1" variant="h3" weight="bold">
                  {pageTitle}
                </Typography>
                <Typography variant="body-1">{pageDescription}</Typography>

                <div
                  style={{
                    backgroundImage: `
          linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
        `,
                    backgroundSize: '40px 40px',
                    backgroundColor: '#FFF'
                  }}
                  className="h-[60%] max-h-[500px] overflow-y-auto cavela-scrollbar"
                >
                  {/* Files List with Captions */}
                  {files.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <AnimatePresence>
                        {files.map((fileWithCaption, index) => {
                          // Find the current modal item and check if it matches this file
                          const currentModalItem =
                            modalItems[currentCaptionIndex];
                          const isSelected =
                            isCaptionModalOpen &&
                            currentModalItem &&
                            currentModalItem.id === fileWithCaption.id;

                          const pendingFile = fileWithCaption.pendingFileId
                            ? getPendingFile(fileWithCaption.pendingFileId)
                            : undefined;

                          const uploadStatusIndicator =
                            pendingFile?.status === 'uploading' ? (
                              <div className="mt-2">
                                <Progress
                                  value={pendingFile.progress}
                                  size="sm"
                                />
                              </div>
                            ) : pendingFile?.status === 'error' ? (
                              <UploadFailedNotice
                                error={pendingFile.error}
                                onRetry={() => retryUpload(pendingFile.id)}
                                onRemove={() => handleRemoveFile(index)}
                              />
                            ) : null;

                          return (
                            <motion.div
                              key={fileWithCaption.id}
                              initial={{ opacity: 0, y: -8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              layout
                              transition={{
                                layout: {
                                  duration: 0.3,
                                  ease: [0.4, 0, 0.2, 1]
                                },
                                opacity: { duration: 0.2, ease: 'easeOut' },
                                y: { duration: 0.2, ease: 'easeOut' }
                              }}
                              className={cn(
                                'group flex items-center gap-4 p-4 bg-gray-1 rounded-xl cursor-pointer hover:bg-gray-2 transition-colors overflow-hidden',
                                isSelected && 'bg-gray-4'
                              )}
                              onClick={() => handleEditCaptionWrapper(index)}
                            >
                              {isImage(fileWithCaption.file) ? (
                                <img
                                  src={fileWithCaption.preview}
                                  alt={fileWithCaption.file.name}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-2 group-hover:bg-gray-4 rounded-lg flex items-center justify-center transition-colors">
                                  <DocumentIcon className="size-8 group-hover:text-primary transition-colors text-gray-500" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                {fileWithCaption.caption ? (
                                  <Typography variant="body-2" weight="medium">
                                    {fileWithCaption.caption}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="body-2"
                                    className="text-secondary-text"
                                  >
                                    No caption
                                  </Typography>
                                )}
                                <Typography
                                  variant="body-2"
                                  className="text-secondary-text truncate"
                                >
                                  {fileWithCaption.file.name}
                                </Typography>

                                {isUnprocessableForRfqExtraction(
                                  fileWithCaption.file.name
                                ) && <UnprocessableFileWarning />}

                                {uploadStatusIndicator}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFile(index);
                                }}
                                className="flex-shrink-0 p-2 hover:bg-gray-3 rounded-full transition-colors"
                                aria-label="Remove file"
                              >
                                <XMarkIcon className="size-5 text-gray-600" />
                              </button>
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={onClose}
                    className="flex items-center gap-2 text-gray-600 hover:text-dark transition-colors"
                  >
                    <ChevronLeftIcon className="size-5" />
                    <Typography variant="body-1" weight="bold">
                      I don&apos;t have anything yet
                    </Typography>
                  </button>

                  <ButtonV2
                    onPress={handleSend}
                    isDisabled={
                      files.length === 0 || isSending || isUploadingFiles
                    }
                    isLoading={isSending}
                    size="lg"
                    className="w-36 h-14 rounded-full px-6"
                  >
                    Submit
                  </ButtonV2>
                </div>
              </div>

              {/* right side */}
              <div
                className="flex flex-col gap-4 relative overflow-auto px-6 pt-[132px]"
                style={{
                  backgroundImage: `
          linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
        `,
                  backgroundSize: '40px 40px',
                  backgroundColor: '#FFF'
                }}
              >
                <div
                  className={`h-[420px] cursor-pointer flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-4 p-12 transition-all duration-300 ${
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-4 bg-gray-1'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="bg-gray-200 size-16 flex items-center justify-center rounded-full mb-4">
                    <ArrowUpTrayIcon className="size-6 text-gray-600" />
                  </div>
                  <Typography variant="h6" weight="bold" className="mb-1">
                    Choose files to upload
                  </Typography>
                  <Typography variant="body-2" className="text-secondary-text">
                    We support PDF, DOC, DOCX, XLSX, AI, and images
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </div>
                {/* Uploaded Files Preview */}
                {/* {files.length > 0 && (
                  <div className="flex flex-wrap gap-4">
                    {files.map((fileWithCaption, index) => (
                      <div
                        key={index}
                        className="relative group rounded-lg overflow-hidden border border-gray-4"
                      >
                        {isImage(fileWithCaption.file) ? (
                          <img
                            src={fileWithCaption.preview}
                            alt={fileWithCaption.file.name}
                            className="w-36 h-36 object-cover"
                          />
                        ) : (
                          <div className="w-36 h-36 bg-gray-2 flex items-center justify-center">
                            <Typography
                              variant="body-2"
                              className="text-center px-2"
                            >
                              {fileWithCaption.file.name}
                            </Typography>
                          </div>
                        )}
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="absolute top-1 right-1 bg-black/70 hover:bg-black rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove file"
                        >
                          <XMarkIcon className="size-4 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )} */}
              </div>
            </div>
          </div>

          {/* Caption Modal */}
          <CaptionModal
            placeholder="What should we take from this file?"
            isOpen={isCaptionModalOpen}
            onClose={() => {
              // Clear pending files if modal is closed without adding
              if (pendingFileUploads.length > 0) {
                // Revoke preview URLs for pending files
                pendingFileUploads.forEach((file) => {
                  if (file.id) {
                    removePreview(file.id);
                  }
                });
                setPendingFileUploads([]);
                setCaptionValue('');
              }
              setIsShowingNewFilesOnly(false);
              setNewFileIds(new Set());
              onCloseCaptionModal();
            }}
            items={modalItems}
            currentIndex={currentCaptionIndex}
            captionValue={captionValue}
            onCaptionChange={setCaptionValue}
            isEditMode={isEditMode}
            onSave={handleCaptionSave}
            onNext={handleCaptionNext}
            onPrevious={handleCaptionPrevious}
            onDone={handleCaptionDone}
            renderPreview={renderPreview}
            getPreviewUrl={getPreviewUrl}
            width="w-1/2"
            isFullScreen={isFullScreenPreview}
            warning={currentItemWarning}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default RfqFilesUpload;
